import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  FaWallet, FaArrowUp, FaArrowDown, FaCheckCircle,
  FaClock, FaHistory, FaReceipt, FaDownload,
} from "react-icons/fa";
import jsPDF from "jspdf";

const norm = s => (s || "").toLowerCase().replace(/[_\s]/g, "");

const money = n =>
  `KSh ${Number(n || 0).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  })}`;

const jobAmount = j =>
  Number(j.payment || j.amount || j.budget || 0);

const jobDate = j =>
  new Date(j.completed_at || j.updated_at || 0);

function CountUp({ value, duration = 1200, prefix = "" }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    let raf;
    let start;

    const target = Number(value) || 0;

    const tick = ts => {
      if (!start) start = ts;

      const p = Math.min(
        (ts - start) / duration,
        1
      );

      setDisplay(
        target * (1 - Math.pow(1 - p, 3))
      );

      if (p < 1) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  return (
    <>
      {prefix}
      {Math.round(display).toLocaleString()}
    </>
  );
}

function StatusPill({ status }) {
  const cfg = {
    paid: {
      bg: "#fff000",
      color: "#000000",
      label: "Paid",
      dot: "#000000",
    },

    completed: {
      bg: "#eff6ff",
      color: "#1e40af",
      label: "Awaiting Payment",
      dot: "#3b82f6",
    },

    paymentpending: {
      bg: "#fff000",
      color: "#000000",
      label: "Payment Pending",
      dot: "#000000",
    },
  }[norm(status)] || {
    bg: "#f3f4f6",
    color: "#6b7280",
    label: status || "Unknown",
    dot: "#9ca3af",
  };

  return (
    <span
      style={{
        background: cfg.bg,
        color: cfg.color,
        padding: "3px 10px",
        borderRadius: 50,
        fontSize: ".72rem",
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      {cfg.label}
    </span>
  );
}

const RANGES = [
  { id: "thisMonth", label: "This Month" },
  { id: "last3", label: "Last 3 Months" },
  { id: "thisYear", label: "This Year" },
  { id: "all", label: "All Time" },
];

function rangeStart(rangeId, now) {
  if (rangeId === "thisMonth") {
    return new Date(
      now.getFullYear(),
      now.getMonth(),
      1
    );
  }

  if (rangeId === "last3") {
    return new Date(
      now.getFullYear(),
      now.getMonth() - 2,
      1
    );
  }

  if (rangeId === "thisYear") {
    return new Date(
      now.getFullYear(),
      0,
      1
    );
  }

  return null;
}

function FinanceTab({
  craftsman = {},
  jobs = [],
  addToast,
}) {
  const [visible, setVisible] = useState(false);
  const [range, setRange] = useState("thisMonth");

  useEffect(() => {
    const t = setTimeout(
      () => setVisible(true),
      60
    );

    return () => clearTimeout(t);
  }, []);

  const stats = useMemo(() => {
    const paid = jobs.filter(
      j => norm(j.status) === "paid"
    );

    const completedUnpaid = jobs.filter(j =>
      ["completed", "paymentpending"].includes(
        norm(j.status)
      )
    );

    const totalEarned = paid.reduce(
      (s, j) => s + jobAmount(j),
      0
    );

    const pendingClearance = completedUnpaid.reduce(
      (s, j) => s + jobAmount(j),
      0
    );

    const now = new Date();

    const thisMonth = paid
      .filter(j => {
        const d = jobDate(j);

        return (
          d.getMonth() === now.getMonth() &&
          d.getFullYear() === now.getFullYear()
        );
      })
      .reduce(
        (s, j) => s + jobAmount(j),
        0
      );

    const lastMonthDate = new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      1
    );

    const lastMonth = paid
      .filter(j => {
        const d = jobDate(j);

        return (
          d.getMonth() ===
            lastMonthDate.getMonth() &&
          d.getFullYear() ===
            lastMonthDate.getFullYear()
        );
      })
      .reduce(
        (s, j) => s + jobAmount(j),
        0
      );

    const momChange =
      lastMonth > 0
        ? ((thisMonth - lastMonth) /
            lastMonth) *
          100
        : thisMonth > 0
        ? 100
        : 0;

    const byService = {};

    paid.forEach(j => {
      const key = j.service || "Other";

      byService[key] =
        (byService[key] || 0) +
        jobAmount(j);
    });

    const serviceBreakdown = Object.entries(
      byService
    )
      .map(([service, amount]) => ({
        service,
        amount,
      }))
      .sort(
        (a, b) => b.amount - a.amount
      )
      .slice(0, 5);

    const monthly = [];

    for (let i = 5; i >= 0; i--) {
      const d = new Date(
        now.getFullYear(),
        now.getMonth() - i,
        1
      );

      const total = paid
        .filter(j => {
          const jd = jobDate(j);

          return (
            jd.getMonth() === d.getMonth() &&
            jd.getFullYear() === d.getFullYear()
          );
        })
        .reduce(
          (s, j) => s + jobAmount(j),
          0
        );

      monthly.push({
        label: d.toLocaleDateString(
          undefined,
          { month: "short" }
        ),
        total,
      });
    }

    const txns = [
      ...paid.map(j => ({
        id: `job-${j.id}`,
        title:
          j.service ||
          j.title ||
          "Job payment",
        client: j.client?.full_name,
        date: jobDate(j),
        amount: jobAmount(j),
        status: "paid",
      })),

      ...completedUnpaid.map(j => ({
        id: `pending-${j.id}`,
        title:
          j.service ||
          j.title ||
          "Job payment",
        client: j.client?.full_name,
        date: jobDate(j),
        amount: jobAmount(j),
        status: norm(j.status),
      })),
    ].sort(
      (a, b) =>
        new Date(b.date || 0) -
        new Date(a.date || 0)
    );

    return {
      totalEarned,
      pendingClearance,
      thisMonth,
      momChange,
      txns,
      serviceBreakdown,
      monthly,
      paid,
    };
  }, [jobs]);

  const rangeStats = useMemo(() => {
    const now = new Date();
    const start = rangeStart(
      range,
      now
    );

    const inRange = t =>
      !start ||
      new Date(t.date) >= start;

    const filteredTxns =
      stats.txns.filter(inRange);

    const filteredPaid =
      stats.paid.filter(j =>
        inRange({
          date: jobDate(j),
        })
      );

    const total = filteredPaid.reduce(
      (s, j) => s + jobAmount(j),
      0
    );

    const count = filteredPaid.length;

    const avg =
      count > 0
        ? total / count
        : 0;

    let topService = null;

    if (filteredPaid.length) {
      const byService = {};

      filteredPaid.forEach(j => {
        const key =
          j.service || "Other";

        byService[key] =
          (byService[key] || 0) +
          jobAmount(j);
      });

      topService =
        Object.entries(byService).sort(
          (a, b) => b[1] - a[1]
        )[0][0];
    }

    return {
      filteredTxns,
      total,
      count,
      avg,
      topService,
      start,
    };
  }, [stats, range]);

  const rangeLabel =
    RANGES.find(
      r => r.id === range
    )?.label || "All Time";

  const downloadStatement = () => {
    const doc = new jsPDF();

    let y = 20;
    const lh = 8;

    doc.setFontSize(20);
    doc.setFont(
      "helvetica",
      "bold"
    );

    doc.text(
      "EARNINGS STATEMENT",
      105,
      y,
      { align: "center" }
    );

    y += 12;

    doc.setFontSize(10);
    doc.setFont(
      "helvetica",
      "normal"
    );

    doc.text(
      `Craftsman: ${
        craftsman.full_name ||
        craftsman.company_name ||
        "N/A"
      }`,
      14,
      y
    );

    y += lh;

    doc.text(
      `Period: ${rangeLabel}`,
      14,
      y
    );

    doc.text(
      `Generated: ${new Date().toLocaleDateString()}`,
      120,
      y
    );

    y += lh * 1.5;

    doc.setFillColor(
      255,
      240,
      0
    );

    doc.rect(
      14,
      y - 5,
      182,
      30,
      "F"
    );

    doc.setFont(
      "helvetica",
      "bold"
    );

    doc.setTextColor(
      0,
      0,
      0
    );

    doc.text(
      `Total Earned: ${money(
        rangeStats.total
      )}`,
      20,
      y + 3
    );

    doc.text(
      `Jobs Paid: ${rangeStats.count}`,
      20,
      y + 12
    );

    doc.text(
      `Average per Job: ${money(
        rangeStats.avg
      )}`,
      110,
      y + 3
    );

    doc.text(
      `Top Service: ${
        rangeStats.topService ||
        "N/A"
      }`,
      110,
      y + 12
    );

    doc.setTextColor(
      0,
      0,
      0
    );

    y += 34;

    doc.setFillColor(
      255,
      240,
      0
    );

    doc.rect(
      14,
      y - 5,
      182,
      8,
      "F"
    );

    doc.setTextColor(
      0,
      0,
      0
    );

    doc.setFont(
      "helvetica",
      "bold"
    );

    doc.text(
      "Date",
      16,
      y
    );

    doc.text(
      "Service",
      55,
      y
    );

    doc.text(
      "Client",
      115,
      y
    );

    doc.text(
      "Amount",
      165,
      y
    );

    y += lh;

    doc.setTextColor(
      0,
      0,
      0
    );

    doc.setFont(
      "helvetica",
      "normal"
    );

    if (
      rangeStats.filteredTxns.length ===
      0
    ) {
      doc.text(
        "No transactions in this period.",
        16,
        y
      );

      y += lh;
    } else {
      rangeStats.filteredTxns.forEach(
        t => {
          if (y > 270) {
            doc.addPage();
            y = 20;
          }

          const dateStr = t.date
            ? new Date(
                t.date
              ).toLocaleDateString()
            : "—";

          doc.text(
            dateStr,
            16,
            y
          );

          doc.text(
            (t.title || "").slice(
              0,
              28
            ),
            55,
            y
          );

          doc.text(
            (t.client || "—").slice(
              0,
              22
            ),
            115,
            y
          );

          doc.text(
            money(t.amount),
            165,
            y
          );

          y += lh;
        }
      );
    }

    doc.save(
      `Earnings-Statement-${range}-${Date.now()}.pdf`
    );

    addToast?.(
      "Statement downloaded",
      "success",
      "Downloaded"
    );
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800;900&display=swap');

        .fin-wrap * {
          box-sizing: border-box;
        }

        .fin-wrap {
          font-family: 'Raleway', sans-serif;
          color: #000000;
        }

        .fin-enter {
          opacity: 0;
          transform: translateY(18px);
          transition:
            opacity .5s ease,
            transform .5s cubic-bezier(.34,1.3,.64,1);
        }

        .fin-enter.show {
          opacity: 1;
          transform: translateY(0);
        }

        .fin-hero {
          background: #000000;
          border-radius: 20px;
          padding: 2rem 2.5rem;
          margin-bottom: 1.75rem;
          display: flex;
          flex-wrap: wrap;
          gap: 1.5rem;
          align-items: center;
          justify-content: space-between;
          position: relative;
          overflow: hidden;
        }

        .fin-hero::before {
          content: '';
          position: absolute;
          top: -50px;
          right: -50px;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background: radial-gradient(
            circle,
            rgba(255,240,0,.18),
            transparent 70%
          );
        }

        .fin-hero-label {
          color: #ffffff;
          font-size: .8rem;
          font-weight: 600;
          margin-bottom: 4px;
        }

        .fin-hero-amount {
          color: #fff000;
          font-size: 2.4rem;
          font-weight: 800;
          letter-spacing: -1px;
        }

        .fin-hero-sub {
          color: #fff000;
          font-size: .8rem;
          font-weight: 600;
          margin-top: 6px;
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .fin-stats {
          display: grid;
          grid-template-columns: repeat(
            auto-fit,
            minmax(190px, 1fr)
          );
          gap: 1rem;
          margin-bottom: 1.75rem;
        }

        .fin-stat {
          background: #ffffff;
          border: 1px solid #f0f0ef;
          border-radius: 16px;
          padding: 1.25rem;
          box-shadow: 0 2px 10px rgba(0,0,0,.03);
        }

        .fin-stat-icon {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          margin-bottom: .6rem;
        }

        .fin-stat-label {
          font-size: .75rem;
          color: #9ca3af;
          font-weight: 600;
        }

        .fin-stat-value {
          font-size: 1.4rem;
          font-weight: 800;
          color: #000000;
          margin: 2px 0;
        }

        .fin-panel {
          background: #ffffff;
          border: 1px solid #f0f0ef;
          border-radius: 18px;
          box-shadow: 0 2px 10px rgba(0,0,0,.03);
          margin-bottom: 1.5rem;
          overflow: hidden;
        }

        .fin-panel-head {
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid #f5f5f4;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: .75rem;
        }

        .fin-panel-title {
          font-weight: 800;
          font-size: .9rem;
          display: flex;
          align-items: center;
          gap: 8px;
          color: #000000;
        }

        .fin-range-group {
          display: flex;
          gap: .4rem;
          flex-wrap: wrap;
        }

        .fin-range-btn {
          border: 1.5px solid #e5e7eb;
          background: #ffffff;
          color: #6b7280;
          padding: 5px 12px;
          border-radius: 50px;
          font-size: .75rem;
          font-weight: 700;
          cursor: pointer;
          transition: all .15s;
          font-family: 'Raleway', sans-serif;
        }

        .fin-range-btn:hover {
          border-color: #fff000;
          color: #000000;
        }

        .fin-range-btn.active {
          background: #fff000;
          border-color: #fff000;
          color: #000000;
        }

        .fin-download-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #fff000;
          color: #000000;
          border: none;
          padding: 8px 16px;
          border-radius: 10px;
          font-family: 'Raleway', sans-serif;
          font-weight: 800;
          font-size: .78rem;
          cursor: pointer;
          transition: transform .15s;
        }

        .fin-download-btn:hover {
          transform: translateY(-1px);
        }

        .fin-txn-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: .9rem 1.5rem;
          border-bottom: 1px solid #f5f5f4;
        }

        .fin-txn-row:last-child {
          border-bottom: none;
        }

        .fin-txn-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .fin-txn-title {
          font-size: .85rem;
          font-weight: 700;
          color: #000000;
          margin: 0;
        }

        .fin-txn-date {
          font-size: .72rem;
          color: #9ca3af;
          margin: 1px 0 0;
        }

        .fin-txn-amount {
          font-size: .9rem;
          font-weight: 800;
          margin-left: auto;
        }

        .fin-empty {
          text-align: center;
          padding: 3rem 1rem;
          color: #9ca3af;
        }
      `}</style>

      <div className="fin-wrap">

        <div
          className={`fin-hero fin-enter ${
            visible ? "show" : ""
          }`}
        >
          <div
            style={{
              position: "relative",
              zIndex: 1,
            }}
          >
            <div className="fin-hero-label">
              <FaWallet
                size={12}
                style={{ marginRight: 6 }}
              />
              Total earned
            </div>

            <div className="fin-hero-amount">
              {visible ? (
                <CountUp
                  value={stats.totalEarned}
                  prefix="KSh "
                  duration={1400}
                />
              ) : (
                "KSh 0"
              )}
            </div>

            <div className="fin-hero-sub">
              {stats.momChange >= 0 ? (
                <FaArrowUp size={10} />
              ) : (
                <FaArrowDown size={10} />
              )}

              {Math.abs(
                stats.momChange
              ).toFixed(0)}
              % vs last month ·{" "}
              {money(stats.thisMonth)} this month
            </div>
          </div>
        </div>

        <div className="fin-stats">
          {[
            {
              icon: <FaCheckCircle />,
              color: "#000000",
              bg: "#fff000",
              label:
                "Total earned (paid jobs)",
              value: stats.totalEarned,
            },
            {
              icon: <FaClock />,
              color: "#000000",
              bg: "#fff000",
              label: "Awaiting payment",
              value: stats.pendingClearance,
            },
            {
              icon: <FaWallet />,
              color: "#000000",
              bg: "#fff000",
              label: "This month",
              value: stats.thisMonth,
            },
          ].map((s, i) => (
            <div
              key={i}
              className={`fin-stat fin-enter ${
                visible ? "show" : ""
              }`}
              style={{
                transitionDelay: `${i * 80}ms`,
              }}
            >
              <div
                className="fin-stat-icon"
                style={{
                  background: s.bg,
                  color: s.color,
                }}
              >
                {s.icon}
              </div>

              <div className="fin-stat-label">
                {s.label}
              </div>

              <div className="fin-stat-value">
                {money(s.value)}
              </div>
            </div>
          ))}
        </div>

        <div
          className={`fin-panel fin-enter ${
            visible ? "show" : ""
          }`}
          style={{
            transitionDelay: "120ms",
          }}
        >
          <div className="fin-panel-head">
            <div className="fin-panel-title">
              <FaCheckCircle size={12} />
              Earnings by Service
            </div>
          </div>

          <div
            style={{
              padding: "1.25rem 1.5rem",
            }}
          >
            {stats.serviceBreakdown.length ===
            0 ? (
              <p
                style={{
                  color: "#9ca3af",
                  fontSize: ".85rem",
                  margin: 0,
                }}
              >
                No paid jobs yet.
              </p>
            ) : (
              stats.serviceBreakdown.map(
                s => {
                  const pct =
                    stats.totalEarned > 0
                      ? (s.amount /
                          stats.totalEarned) *
                        100
                      : 0;

                  return (
                    <div
                      key={s.service}
                      style={{
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent:
                            "space-between",
                          fontSize: ".82rem",
                          marginBottom: 4,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 700,
                            color: "#374151",
                          }}
                        >
                          {s.service}
                        </span>

                        <span
                          style={{
                            fontWeight: 700,
                            color: "#000000",
                          }}
                        >
                          {money(s.amount)}
                        </span>
                      </div>

                      <div
                        style={{
                          background: "#f3f4f6",
                          borderRadius: 50,
                          height: 6,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${pct}%`,
                            height: "100%",
                            background: "#fff000",
                            borderRadius: 50,
                          }}
                        />
                      </div>
                    </div>
                  );
                }
              )
            )}

            <div
              style={{
                marginTop: "1.5rem",
                paddingTop: "1.25rem",
                borderTop:
                  "1px solid #f5f5f4",
              }}
            >
              <div
                style={{
                  fontSize: ".72rem",
                  fontWeight: 700,
                  color: "#9ca3af",
                  marginBottom: 10,
                  textTransform:
                    "uppercase",
                  letterSpacing: .5,
                }}
              >
                Last 6 months
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 8,
                  height: 60,
                }}
              >
                {stats.monthly.map(
                  (m, i) => {
                    const max = Math.max(
                      ...stats.monthly.map(
                        x => x.total
                      ),
                      1
                    );

                    const h = Math.max(
                      (m.total / max) *
                        100,
                      4
                    );

                    return (
                      <div
                        key={i}
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection:
                            "column",
                          alignItems:
                            "center",
                          gap: 4,
                        }}
                      >
                        <div
                          title={money(
                            m.total
                          )}
                          style={{
                            width: "100%",
                            height: `${h}%`,
                            background:
                              "#fff000",
                            borderRadius:
                              "4px 4px 0 0",
                            minHeight: 3,
                          }}
                        />

                        <span
                          style={{
                            fontSize: ".65rem",
                            color: "#9ca3af",
                            fontWeight: 600,
                          }}
                        >
                          {m.label}
                        </span>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          className={`fin-panel fin-enter ${
            visible ? "show" : ""
          }`}
          style={{
            transitionDelay: "180ms",
          }}
        >
          <div className="fin-panel-head">
            <div className="fin-panel-title">
              <FaHistory size={12} />
              Job Payment History
            </div>

            <div className="fin-range-group">
              {RANGES.map(r => (
                <button
                  key={r.id}
                  className={`fin-range-btn ${
                    range === r.id
                      ? "active"
                      : ""
                  }`}
                  onClick={() =>
                    setRange(r.id)
                  }
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div
            style={{
              padding: "1rem 1.5rem",
              background: "#fafaf9",
              borderBottom:
                "1px solid #f5f5f4",
              display: "flex",
              alignItems: "center",
              justifyContent:
                "space-between",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div
              style={{
                fontSize: ".82rem",
                color: "#374151",
              }}
            >
              <strong
                style={{
                  color: "#000000",
                }}
              >
                {money(
                  rangeStats.total
                )}
              </strong>{" "}
              from{" "}
              <strong>
                {rangeStats.count}
              </strong>{" "}
              job
              {rangeStats.count !== 1
                ? "s"
                : ""}{" "}
              · {rangeLabel}
            </div>

            <button
              className="fin-download-btn"
              onClick={downloadStatement}
            >
              <FaDownload size={12} />
              Download Statement
            </button>
          </div>

          {rangeStats.filteredTxns
            .length === 0 ? (
            <div className="fin-empty">
              <FaReceipt
                size={34}
                style={{
                  opacity: .15,
                  display: "block",
                  margin:
                    "0 auto 1rem",
                }}
              />

              <p
                style={{
                  fontWeight: 700,
                  color: "#374151",
                  margin: 0,
                }}
              >
                No transactions in this
                period
              </p>

              <p
                style={{
                  fontSize: ".85rem",
                  margin:
                    ".25rem 0 0",
                }}
              >
                Try a wider date range
              </p>
            </div>
          ) : (
            rangeStats.filteredTxns
              .slice(0, 30)
              .map(t => (
                <div
                  key={t.id}
                  className="fin-txn-row"
                >
                  <div
                    className="fin-txn-icon"
                    style={{
                      background:
                        "#fff000",
                      color:
                        "#000000",
                    }}
                  >
                    <FaArrowUp size={13} />
                  </div>

                  <div
                    style={{
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <p className="fin-txn-title">
                      {t.title}
                      {t.client
                        ? ` · ${t.client}`
                        : ""}
                    </p>

                    <p className="fin-txn-date">
                      {t.date
                        ? new Date(
                            t.date
                          ).toLocaleDateString(
                            undefined,
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            }
                          )
                        : "—"}
                    </p>
                  </div>

                  <StatusPill
                    status={t.status}
                  />

                  <span
                    className="fin-txn-amount"
                    style={{
                      color:
                        "#000000",
                    }}
                  >
                    +{money(t.amount)}
                  </span>
                </div>
              ))
          )}
        </div>
      </div>
    </>
  );
}

FinanceTab.propTypes = {
  craftsman: PropTypes.object,
  jobs: PropTypes.array,
  addToast: PropTypes.func,
};

export default FinanceTab;